import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import PublicListings from "@/pages/PublicListings";
import Dashboard from "@/pages/Dashboard";
import People from "@/pages/People";
import PersonProfile from "@/pages/PersonProfile";
import PropertyDetail from "@/pages/PropertyDetail";
import SalesTracker from "@/pages/SalesTracker";
import SalesAnalytics from "@/pages/SalesAnalytics";
import TransactionManager from "@/pages/TransactionManager";
import Workflows from "@/pages/Workflows";
import TransactionReports from "@/pages/TransactionReports";
import TransactionDetail from "@/pages/TransactionDetail";
import ListingManager from "@/pages/ListingManager";
import ListingEditor from "@/pages/ListingEditor";
import PublicListing from "@/pages/PublicListing";
import AnalyticsReport from "@/pages/AnalyticsReport";
import AgentLanding from "@/pages/AgentLanding";
import SeoPages from "@/pages/SeoPages";
import Training from "@/pages/Training";
import OfficeProcedures from "@/pages/OfficeProcedures";
import AdminUsers from "@/pages/AdminUsers";
import McpKeys from "@/pages/McpKeys";
import { HostRedirect } from "@/lib/site";

const shell = (tool, Page) => (
  <ProtectedRoute tool={tool}>
    <AppLayout>
      <Page />
    </AppLayout>
  </ProtectedRoute>
);

function Root() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFDFB]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1F3C88]" />
      </div>
    );
  }
  return user ? shell(null, Dashboard) : <Landing />;
}

function App() {
  return (
    <div className="App">
      <HostRedirect />
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/browse" element={<PublicListings />} />
            <Route path="/p/:slug" element={<PublicListing />} />
            <Route path="/report/:slug" element={<AnalyticsReport />} />
            <Route path="/a/:username" element={<AgentLanding />} />
            <Route path="/" element={<Root />} />
            <Route path="/people" element={shell(null, People)} />
            <Route path="/people/:id" element={shell(null, PersonProfile)} />
            <Route path="/properties/:id" element={shell(null, PropertyDetail)} />
            <Route path="/sales" element={shell("sales", SalesTracker)} />
            <Route path="/sales/analytics" element={shell("sales", SalesAnalytics)} />
            <Route path="/transactions" element={shell("transactions", TransactionManager)} />
            <Route path="/transactions/workflows" element={shell("transactions", Workflows)} />
            <Route path="/transactions/reports" element={shell("transactions", TransactionReports)} />
            <Route path="/transactions/:id" element={shell("transactions", TransactionDetail)} />
            <Route path="/listings" element={shell("listings", ListingManager)} />
            <Route path="/listings/:id" element={shell("listings", ListingEditor)} />
            <Route path="/seo" element={shell("seo", SeoPages)} />
            <Route path="/training" element={shell("training", Training)} />
            <Route path="/procedures" element={shell("office_procedures", OfficeProcedures)} />
            <Route path="/users" element={shell("users", AdminUsers)} />
            <Route path="/integrations" element={shell(null, McpKeys)} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
